import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import type { Showcase, ShowcaseProperty, ShowcaseResource } from '../../types';
import { LayoutDashboard, Download, ZoomIn, ZoomOut, RotateCcw, Maximize2, X } from 'lucide-react';

interface FloorPlanSectionProps {
  showcase: Showcase;
  property: ShowcaseProperty;
  resources: ShowcaseResource[];
  brandColour: string;
  darkMode?: boolean;
}

function ZoomControls({
  zoomIn,
  zoomOut,
  resetTransform,
  brandColour,
  dm,
}: {
  zoomIn: () => void;
  zoomOut: () => void;
  resetTransform: () => void;
  brandColour: string;
  dm?: boolean;
}) {
  return (
    <div
      className={`absolute bottom-3 right-3 z-10 flex flex-col gap-1 rounded-lg border p-1 shadow-lg backdrop-blur-sm ${
        dm
          ? 'border-white/20 bg-black/60'
          : 'border-slate-200 bg-white/80'
      }`}
    >
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); zoomIn(); }}
        className={`p-2 rounded-md transition-colors ${dm ? 'hover:bg-white/20' : 'hover:bg-slate-100'}`}
        title="Zoom in"
      >
        <ZoomIn size={18} style={{ color: brandColour }} />
      </button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); zoomOut(); }}
        className={`p-2 rounded-md transition-colors ${dm ? 'hover:bg-white/20' : 'hover:bg-slate-100'}`}
        title="Zoom out"
      >
        <ZoomOut size={18} style={{ color: brandColour }} />
      </button>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); resetTransform(); }}
        className={`p-2 rounded-md transition-colors ${dm ? 'hover:bg-white/20' : 'hover:bg-slate-100'}`}
        title="Reset view"
      >
        <RotateCcw size={16} style={{ color: brandColour }} />
      </button>
    </div>
  );
}

function ZoomableFloorPlanImage({
  src,
  brandColour,
  dm,
  onFullScreen,
  maxH,
}: {
  src: string;
  brandColour: string;
  dm?: boolean;
  onFullScreen?: () => void;
  maxH?: string;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [dims, setDims] = useState({ width: 800, height: 600 });

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      const r = el.getBoundingClientRect();
      if (r.width > 0 && r.height > 0) setDims({ width: r.width, height: r.height });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <div
      ref={containerRef}
      className={`relative rounded-xl overflow-hidden shadow-lg ${dm ? 'bg-slate-900' : 'bg-white'}`}
      style={{ minHeight: 320, maxHeight: maxH }}
    >
      <TransformWrapper
        initialScale={1}
        minScale={0.5}
        maxScale={5}
        centerOnInit
        limitToBounds={false}
        doubleClick={{ mode: 'reset' }}
        wheel={{ disabled: true }}
        pinch={{ disabled: true }}
        panning={{ disabled: true }}
      >
        {({ zoomIn, zoomOut, resetTransform }) => (
          <>
            {/* Zoom + / - buttons */}
            <ZoomControls
              zoomIn={zoomIn}
              zoomOut={zoomOut}
              resetTransform={resetTransform}
              brandColour={brandColour}
              dm={dm}
            />

            {/* Full-screen button */}
            {onFullScreen && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); onFullScreen(); }}
                className={`absolute top-3 right-3 z-10 flex items-center gap-2 px-3 py-2 rounded-lg shadow-lg backdrop-blur-sm transition-colors ${
                  dm
                    ? 'border border-white/20 bg-black/60 hover:bg-white/20'
                    : 'border border-slate-200 bg-white/80 hover:bg-slate-100'
                }`}
                title="Open full-screen zoom view"
              >
                <Maximize2 size={15} style={{ color: brandColour }} />
                <span className={`text-xs font-medium ${dm ? 'text-white/80' : 'text-slate-600'}`}>
                  Zoom
                </span>
              </button>
            )}

            <TransformComponent
              wrapperStyle={{
                width: '100%',
                height: '100%',
                minHeight: 320,
                maxHeight: maxH,
                overflow: 'hidden',
              }}
              contentStyle={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: 16,
              }}
            >
              <img
                src={src}
                alt="Floor Plan"
                className="max-w-none block pointer-events-none object-contain select-none"
                draggable={false}
                style={{ maxWidth: dims.width - 32, maxHeight: (maxH ? parseInt(maxH) - 32 : dims.height - 32) }}
              />
            </TransformComponent>
          </>
        )}
      </TransformWrapper>
    </div>
  );
}

function FullScreenFloorPlan({
  src,
  brandColour,
  onClose,
}: {
  src: string;
  brandColour: string;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return createPortal(
    <div
      className="fixed inset-0 bg-black/95 backdrop-blur-sm flex flex-col"
      style={{ zIndex: 99999, touchAction: 'none' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 bg-gradient-to-b from-black/70 to-transparent">
        <div className="flex items-center gap-3">
          <LayoutDashboard size={18} style={{ color: brandColour }} />
          <h2 className="text-sm font-semibold text-white">Floor Plan</h2>
          <span className="text-xs text-white/40 hidden sm:inline">
            Scroll to zoom · Drag to pan · Double-click to reset
          </span>
        </div>
        <button
          onClick={onClose}
          className="p-2 rounded-lg bg-white/10 hover:bg-white/20 transition-colors"
        >
          <X size={18} className="text-white" />
        </button>
      </div>

      {/* Zoomable image */}
      <div className="flex-1 min-h-0 relative">
        <TransformWrapper
          initialScale={1}
          minScale={0.3}
          maxScale={8}
          centerOnInit
          limitToBounds={false}
          doubleClick={{ mode: 'reset' }}
          wheel={{ step: 0.08 }}
          pinch={{ step: 5 }}
          panning={{ velocityDisabled: true }}
        >
          {({ zoomIn, zoomOut, resetTransform }) => (
            <>
              <div className="absolute bottom-6 right-6 z-10 flex flex-col gap-1 rounded-lg border border-white/20 bg-black/60 p-1 shadow-lg backdrop-blur-sm">
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); zoomIn(); }}
                  className="p-2.5 rounded-md hover:bg-white/20 transition-colors"
                  title="Zoom in"
                >
                  <ZoomIn size={20} style={{ color: brandColour }} />
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); zoomOut(); }}
                  className="p-2.5 rounded-md hover:bg-white/20 transition-colors"
                  title="Zoom out"
                >
                  <ZoomOut size={20} style={{ color: brandColour }} />
                </button>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); resetTransform(); }}
                  className="p-2.5 rounded-md hover:bg-white/20 transition-colors"
                  title="Reset view"
                >
                  <RotateCcw size={18} style={{ color: brandColour }} />
                </button>
              </div>

              <TransformComponent
                wrapperStyle={{
                  width: '100%',
                  height: '100%',
                  overflow: 'hidden',
                  touchAction: 'none',
                  cursor: 'grab',
                }}
                contentStyle={{
                  width: '100%',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <img
                  src={src}
                  alt="Floor Plan"
                  className="max-w-none block pointer-events-none object-contain select-none"
                  draggable={false}
                  style={{ maxWidth: '92vw', maxHeight: '85vh' }}
                />
              </TransformComponent>
            </>
          )}
        </TransformWrapper>
      </div>
    </div>,
    document.body
  );
}

export default function FloorPlanSection({ showcase, property, resources, brandColour, darkMode: dm }: FloorPlanSectionProps) {
  const floorPlan = resources[0];
  const [fullScreen, setFullScreen] = useState(false);

  if (!floorPlan) return null;

  const isInteractive = floorPlan.type === 'planos_embed' && floorPlan.embed_url;
  const isPdf = floorPlan.type === 'floorplan_pdf';

  return (
    <section className={`py-16 ${dm ? 'bg-slate-800' : 'bg-slate-50'}`}>
      <div className="max-w-5xl mx-auto px-8">
        <div className="flex items-center justify-between mb-8">
          <h2 className={`text-2xl font-bold ${dm ? 'text-white' : 'text-slate-900'}`}>Floor Plan</h2>
          {isPdf && floorPlan.source_url && (
            <a
              href={floorPlan.source_url}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg text-white transition-colors"
              style={{ backgroundColor: brandColour }}
            >
              <Download className="w-4 h-4" />
              Download PDF
            </a>
          )}
        </div>

        {isInteractive ? (
          <div className={`aspect-[4/3] rounded-xl overflow-hidden shadow-lg ${dm ? 'bg-slate-900' : 'bg-white'}`}>
            <iframe
              src={floorPlan.embed_url!}
              className="w-full h-full"
              allow="fullscreen"
              allowFullScreen
              title="Interactive floor plan"
            />
          </div>
        ) : floorPlan.source_url ? (
          <>
            <ZoomableFloorPlanImage
              src={floorPlan.source_url}
              brandColour={brandColour}
              dm={dm}
              onFullScreen={() => setFullScreen(true)}
              maxH="650px"
            />
            {fullScreen && (
              <FullScreenFloorPlan
                src={floorPlan.source_url}
                brandColour={brandColour}
                onClose={() => setFullScreen(false)}
              />
            )}
          </>
        ) : (
          <div className={`aspect-[4/3] rounded-xl flex items-center justify-center ${dm ? 'bg-slate-800' : 'bg-slate-100'}`}>
            <LayoutDashboard className={`w-16 h-16 ${dm ? 'text-slate-500' : 'text-slate-300'}`} />
          </div>
        )}
      </div>
    </section>
  );
}
